import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { StressResultsService } from '../../services/stress-results.service';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { Observable, Subscription, catchError, forkJoin, map } from 'rxjs';
import { DesignFactorsService } from '../../services/design-factors.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { SafetyFactorResultsUi } from '../../models/perivis-results-state.model';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { SafetyFactorResultModel } from '../../models/safety-factor-results.model';
import { ConnectionSafetyFactorResultsModel } from '../../models/connection-safety-factor-results.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { WellConfigService } from 'src/app/shared/services/well-config.service';

@Component({
  selector: 'app-single-load-safety-factors',
  templateUrl: './single-load-safety-factors.component.html',
  standalone: false
})
export class SingleLoadSafetyFactorsComponent implements OnInit, OnDestroy {

  public safetyFactorResults: SafetyFactorResultModel[];
  private connectionSafetyFactorResults: ConnectionSafetyFactorResultsModel[];
  public loadCases: SelectItem[] = [];
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public modes: SelectItem[] = [
    { label: 'Minimum', value: 'minimum' },
    { label: 'Single', value: 'single' },
    { label: 'Multiple', value: 'multiple' }
  ];
  public errorMsg: string;
  public results: any[];
  public axialResults: any[];
  public resultsBackup: any[];
  public designFactors: any;
  public depthView: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public isLoading = signal<boolean>(true);
  public yAxisTitle = '';
  public xAxisTitle = 'Safety Factor';
  public tableName: string;
  public apiCollapseType: string;
  public userUnits: UserUnitsModel;
  public depthUnit: string;
  public plotName: string;
  public signalRChange: boolean;

  public minimumResultType = 'summary';
  private gridDesignFactorName: string;
  private previousView: string;
  private selectedString: string;
  private _subscriptions: Subscription = new Subscription();

  public types = [
    { label: 'Burst', value: 'barlowBurst' },
    { label: 'Collapse', value: 'apiCollapse' },
    { label: 'Axial', value: 'pureAxial' },
    { label: 'VME (Triaxial)', value: 'vonMisesEquivalent' }
  ]

  public columnDefs: { header: string, field: string }[];
  public columnDefinitions = this.setDepths();

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.safetyFactorResultsStore = this._storeService.createStore(this.componentId, new SafetyFactorResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedMode = this.safetyFactorResultsStore.state.selectedMode || 'minimum';
    this.selectedResultDisplay = this.safetyFactorResultsStore.state.selectedResultDisplay || 'grid';
    this.selectedDepthView = this.safetyFactorResultsStore.state.selectedDepthView || 'md';
    this.includeCharacters = this.safetyFactorResultsStore.state.includeCharacters ?? true;
  }
  get componentId(): string {
    return this._componentId;
  }
  public safetyFactorResultsStore: Store;

  public selectedResultDisplay: string = 'grid';
  public selectedMode: string = 'minimum';
  public selectedDepthView: string = 'md';
  public includeCharacters: boolean = true;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _designFactorsService: DesignFactorsService,
    private _storeService: StoreService,
    private _wellConfigService: WellConfigService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.depthUnit = this.userUnits.longLengths;
    this.yAxisTitle = `MD (${this.userUnits.longLengths})`;

    // this.refreshResults(true, false);

    if (!this.previousView && this.safetyFactorResultsStore.state.selectedMode) {
      this.previousView = this.safetyFactorResultsStore.state?.selectedMode;
    } else {
      this.previousView = 'single'
    }

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this._subscriptions.add(this.safetyFactorResultsStore.subscribe(state => {
      this.selectedResultDisplay = state.selectedResultDisplay;
      this.selectedMode = state.selectedMode;
      this.includeCharacters = state.includeCharacters;
    }));

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.refreshResults(true, false, false);
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: null }));
      //TODO Does this need to be reset to single?
      // this.mode = 'single';
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.refreshResults(true, true);
    }
  }

  private setPlotName(): string {
    if (this.safetyFactorResultsStore.state.selectedMode === 'single') {
      return 'safetyFactorsPlot - ' + this.selectedString + ' - ' + this.safetyFactorResultsStore.state.selectedMode + ' ' +
        (this.safetyFactorResultsStore.state.selectedLoadCase.loadCaseName || this.safetyFactorResultsStore.state.selectedLoadCase.label);
    } else {
      return 'safetyFactorsPlot - ' + this.selectedString + ' - ' + this.safetyFactorResultsStore.state.selectedMode + ' ' + this.safetyFactorResultsStore.state.selectedLoadCase.label;
    }
  }

  public async refreshResults(isLoading: boolean, calledFromSignalR: boolean, calledFromMode?: boolean): Promise<void> {
    this.errorMsg = null;
    this.isLoading.set(isLoading);

    if (calledFromMode && !this.safetyFactorResults) {
      this.isLoading.set(false);
      return;
    }


    if ((!this.safetyFactorResults || calledFromSignalR) && !calledFromMode) {
      this._wellConfigService.getTubular().subscribe({
        next: (tubular) => {
          if (tubular != null) {

            const sources = [
              this._stressResultsService.getAxialResults() as Observable<any>,
              this._stressResultsService.getSafetyFactorResults(),
              this._designFactorsService.getDesignFactors(),
              this._stressResultsService.getConnectionSafetyFactorResults() as Observable<any>
            ];

            forkJoin(sources).pipe(
              map(([axialResults, safetyFactorResults, designFactors, connectionSafetyFactorResults]) => {
                this.resultsBackup = safetyFactorResults;
                this.designFactors = designFactors;
                this.axialResults = axialResults;
                this.connectionSafetyFactorResults = connectionSafetyFactorResults;
                if (safetyFactorResults.length === 0 || safetyFactorResults[0].loadCaseName === '') {
                  this.errorMsg = "Please calculate PERIVIS to see results";
                  this.columnDefs = [];
                  this.results = [];
                  this.safetyFactorResults = [];
                  this.isLoading.set(false);
                  return;
                }
                this.safetyFactorResults = safetyFactorResults;
                this.designFactors = designFactors;

                this.apiCollapseType = this.safetyFactorResults[0].apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse';
                this.remapData();
              }),
              catchError(err => {
                this.errorMsg = "Well Configuration Missing: Please specify at least one string";
                this.isLoading.set(false);
                return err;
              })).subscribe();
          } else {
            this.isLoading.set(false);
            this.errorMsg = "Well Configuration Missing: Please specify at least one string";
          }
        }
      });
    } else {
      this.remapData();
    }
  }

  private remapData() {
    if (!this.safetyFactorResults) {
      this.isLoading.set(false);
      return;
    }
    this.minimumResultType = this.safetyFactorResultsStore.state.selectedLoadCase?.value || this.safetyFactorResultsStore.state.selectedLoadCase;
    if (this.safetyFactorResultsStore.state.selectedMode == "minimum" && this.minimumResultType == null) {
      this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: { label: 'Summary', value: 'summary' } }));
      this.minimumResultType = 'summary';
    }
    if (this.safetyFactorResultsStore.state.selectedMode == "single") {
      if (this.safetyFactorResultsStore.state.selectedLoadCase == null) {
        this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      }
      this.handleSingleSafetyFactorLoad(this.safetyFactorResults, true);
    } else if (this.safetyFactorResultsStore.state.selectedMode == 'multiple') {
      if (this.safetyFactorResultsStore.state.selectedLoadCase == null) {
        this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: { label: 'Barlow Burst', value: 'barlowBurst' } }));
      }
      this.handleMultiSafetyFactorLoad(this.safetyFactorResults, this.designFactors)
    } else if (this.safetyFactorResultsStore.state.selectedMode == 'minimum' && this.minimumResultType !== 'barlowBurst' && !this.minimumResultType?.startsWith('apiCollapse') &&
      this.minimumResultType !== 'pureAxial' && this.minimumResultType !== 'vonMisesEquivalent') {
      if (this.safetyFactorResultsStore.state.selectedLoadCase == null) {
        this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: { label: 'Barlow Burst', value: 'barlowBurst' } }));
      }
      this.handleMinimumSummary(this.safetyFactorResults, this.designFactors);
    } else {
      if (this.safetyFactorResultsStore.state.selectedLoadCase == null) {
        this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: { label: 'Summary', value: 'summary' } }));
      }
      this.handleMinimum(this.safetyFactorResults, this.designFactors, this.minimumResultType);
    }
    this.signalRChange = true;
    this.plotName = this.setPlotName();
    this.isLoading.set(false);
  }

  setLoadCase(e): void {
    this.minimumResultType = e.value;
    if (this.safetyFactorResultsStore.state.selectedMode === "single") {
      this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.results = e.value.value.safetyFactorResults;
    } else {
      this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.refreshResults(e.value, false);
    }

    if (this.safetyFactorResultsStore.state.selectedMode === 'minimum' && e.value.value !== 'summary') {
      this.handleMinimum(this.resultsBackup, this.designFactors, e.value);
    }

    if (this.safetyFactorResultsStore.state.selectedMode === 'minimum' && e.value.value == 'summary') {
      this.handleMinimumSummary(this.resultsBackup, this.designFactors);
    }
  }

  public onSelectedResultsDisplayChange(e): void {
    this.selectedResultDisplay = e.value;
    this.safetyFactorResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
    this.plotName = this.setPlotName();
  }

  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.safetyFactorResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  public onModeChange(e) {
    const newMode = e.value;
    const comingFromMinimumOrSummary = this.previousView === 'minimum' || this.previousView === 'summary';
    const comingFromMultiple = this.previousView === 'multiple';
    // const comingFromSingle = this.previousView === 'single';

    // ✅ Reset result display when returning from minimum/summary to single
    if (newMode === 'single' && comingFromMinimumOrSummary) {
      this.selectedResultDisplay = 'plot';
      this.safetyFactorResultsStore.update(state => ({
        ...state,
        selectedResultDisplay: 'plot'
      }));
    }

    // ✅ Update selectedMode
    this.safetyFactorResultsStore.update(state => ({
      ...state,
      selectedMode: newMode
    }));

    // ✅ Set selectedLoadCase depending on new mode
    let selectedLoadCaseUpdate = null;
    if (newMode === 'minimum') {
      selectedLoadCaseUpdate = { label: 'Summary', value: 'summary' };
    } else if (newMode === 'multiple') {
      selectedLoadCaseUpdate = { label: 'Barlow Burst', value: 'barlowBurst' };
    }

    if (selectedLoadCaseUpdate) {
      this.safetyFactorResultsStore.update(state => ({
        ...state,
        selectedLoadCase: selectedLoadCaseUpdate
      }));
    }

    // ✅ Reset and remap data
    if (newMode === 'minimum' && !comingFromMultiple) {
      this.handleMinimumSummary(this.resultsBackup, this.designFactors);
    } else if (newMode === 'single') {
      this.remapData(); // Always remap for single
    }

    // ✅ Update axis title if leaving minimum/multiple
    if (newMode !== 'minimum') {
      this.xAxisTitle = 'Safety Factor';
    }

    // ✅ Plot name and refresh logic
    const type = this.types.findIndex(
      x => x.value === this.safetyFactorResultsStore.state.selectedLoadCase
    ) > 0;

    this.plotName = this.setPlotName();
    this.refreshResults(
      type ? this.safetyFactorResultsStore.state.selectedLoadCase : { label: 'Barlow Burst', value: 'barlowBurst' },
      false,
      true
    );

    // ✅ Store previous view for next transition
    this.previousView = newMode;
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  private handleSingleSafetyFactorLoad(safetyFactorResults: any[], calledFromSignalR: boolean) {
    if (calledFromSignalR) {
      //TODO Might need to fix this
      // this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
    }
    this.minimumResultType = 'summary';
    this.columnDefs = [
      { header: 'Burst', field: 'barlowBurst' },
      { header: 'Collapse', field: safetyFactorResults[0].apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse' },
      { header: 'Axial', field: 'pureAxial' },
      { header: 'VME (Triaxial)', field: 'vonMisesEquivalent' },
    ];

    this.columnDefinitions = this.setDepths();
    this.columnDefinitions.push(
      {
        header: 'Barlow Burst', field: 'barlowBurst',
        valueFormatter: (params: any) => params
      },
      {
        header: 'API Collapse', field: safetyFactorResults[0].apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse',
        valueFormatter: (params: any) => params
      },
      {
        header: 'Axial', field: 'pureAxial',
        valueFormatter: (params: any) => params
      },
      {
        header: 'VME (Triaxial)', field: 'vonMisesEquivalent',
        valueFormatter: (params: any) => params
      }
    );

    this.results = [];

    this.loadCases = safetyFactorResults?.map(lc => {
      lc.safetyFactorResults.forEach(element => {
        // Axial
        const designFactorAxialCheck = this.safetyFactorResultsStore.state.includeCharacters && element['pureAxial'] < this.getDesignFactor(this.designFactors, 'pureAxial', safetyFactorResults[0].apiCollapseType) - 0.000001 ? ' *' : '';
        if (element['pureAxial'].toString().includes('*')) {
          const pureAxial = element['pureAxial'].toString().substring(0, element['pureAxial'].toString().indexOf(' '));
          element['pureAxial'] = this.roundValue(pureAxial, 3) + designFactorAxialCheck;
        } else {
          element['pureAxial'] = this.roundValue(element['pureAxial'], 3);
        }

        // Collapse
        const collapseType = safetyFactorResults[0].apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse'
        const designFactorCollapseCheck = this.safetyFactorResultsStore.state.includeCharacters && element[collapseType] < this.getDesignFactor(this.designFactors, 'apiCollapse2015', safetyFactorResults[0].apiCollapseType) - 0.000001 ? ' *' : '';
        if (element[collapseType].toString().includes('*')) {
          const collapse = element[collapseType].toString().substring(0, element[collapseType].toString().indexOf(' '));
          element[collapseType] = this.roundValue(collapse, 3) + designFactorCollapseCheck;
        } else {
          element[collapseType] = this.roundValue(element[collapseType], 3);
        }

        // Burst
        const designFactorBurstCheck = this.safetyFactorResultsStore.state.includeCharacters && element['barlowBurst'] < this.getDesignFactor(this.designFactors, 'barlowBurst', safetyFactorResults[0].apiCollapseType) - 0.000001 ? ' *' : '';
        if (element['barlowBurst'].toString().includes('*')) {
          const burst = element['barlowBurst'].toString().substring(0, element['barlowBurst'].toString().indexOf(' '));
          element['barlowBurst'] = this.roundValue(burst, 3) + designFactorBurstCheck;
        } else {
          element['barlowBurst'] = this.roundValue(element['barlowBurst'], 3);
        }

        // VME
        const burstValueAtDepth = element.barlowBurst;
        const collapseValueAtDepth = element[collapseType];
        const designFactorVmeCheck = this.safetyFactorResultsStore.state.includeCharacters && element['vonMisesEquivalent'] < this.getDesignFactor(this.designFactors, 'vonMisesEquivalent', safetyFactorResults[0].apiCollapseType, burstValueAtDepth, collapseValueAtDepth) - 0.000001 ? ' *' : '';
        if (element['vonMisesEquivalent'].toString().includes('*')) {
          const vme = element['vonMisesEquivalent'].toString().substring(0, element['vonMisesEquivalent'].toString().indexOf(' '));
          element['vonMisesEquivalent'] = this.roundValue(vme, 3) + designFactorVmeCheck;
        } else {
          element['vonMisesEquivalent'] = this.roundValue(element['vonMisesEquivalent'], 3);
        }
      });

      return { label: lc.loadCaseName, value: lc };
    }) ?? [];
    if (this.previousView != 'single' || !this.safetyFactorResultsStore.state.selectedLoadCase || !this.safetyFactorResultsStore.state.selectedLoadCase.loadCaseName) {
      this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      this.plotName = this.setPlotName();
    }
    this.previousView = 'single'
    this.results = this.safetyFactorResultsStore.state.selectedLoadCase?.value?.safetyFactorResults || this.safetyFactorResultsStore.state.selectedLoadCase?.safetyFactorResults || [];
  }

  private handleMultiSafetyFactorLoad(safetyFactorResults: any[], designFactors) {
    this.minimumResultType = 'summary';
    this.loadCases = [];
    this.columnDefinitions = this.setDepths();
    this.columnDefs = [];
    this.results = [];
    let i = 0;
    this.loadCases = this.types;

    if (this.previousView !== 'multiple') {
      this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      this.plotName = this.setPlotName();
    }

    this.previousView = 'multiple';

    safetyFactorResults.forEach(load => {
      const loadName: string = load.loadCaseName;
      const loadNameCamel = toCamelCaseString(loadName);
      let selectedType = this.safetyFactorResultsStore.state.selectedLoadCase?.value ?? this.safetyFactorResultsStore.state.selectedLoadCase.value ?? 'barlowBurst';

      this.setAxisNames(selectedType);

      this.columnDefs.push({ header: load.loadCaseName, field: loadNameCamel });

      this.columnDefinitions.push({
        header: loadName, field: loadNameCamel,
        valueFormatter: (params: any) => params
      }
      );

      if (selectedType?.startsWith('apiCollapse')) {
        selectedType = safetyFactorResults[0].apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse'
      }

      const backupArray = [];
      load.safetyFactorResults.forEach(loadResult => {
        const burstValueAtDepth = loadResult.barlowBurst;
        const collapseValueAtDepth = loadResult[selectedType];
        const result = {
          [loadNameCamel]: this.safetyFactorResultsStore.state.includeCharacters ? this.roundValue(loadResult[selectedType], 3) + (loadResult[selectedType] < (this.getDesignFactor(designFactors, selectedType, safetyFactorResults[0].apiCollapseType, burstValueAtDepth, collapseValueAtDepth) - 0.000001) ? ' *' : '') : this.roundValue(loadResult[selectedType], 3),
          measuredDepth: loadResult.measuredDepth,
          trueVerticalDepth: loadResult.trueVerticalDepth
        }

        if (i === 0) {
          this.results.push(result);
        } else {
          backupArray.push(result);
        }
      });

      this.results = this.results.map(x => {
        const otherLoad = backupArray.find(e => e.measuredDepth === x.measuredDepth)
        return { ...x, ...otherLoad }
      });

      if (i === safetyFactorResults.length - 1) {
        const burstValueAtDepth = load.safetyFactorResults.find(x => Math.abs(x.measuredDepth - this.results[this.results.length - 1].measuredDepth) < 0.001).barlowBurst;
        const collapseValueAtDepth = load.safetyFactorResults.find(x => Math.abs(x.measuredDepth - this.results[this.results.length - 1].measuredDepth) < 0.001)[selectedType];
        this.addDesignFactor(selectedType, this.gridDesignFactorName, designFactors, burstValueAtDepth, collapseValueAtDepth);
      }
      i++;
    });
    this.isLoading.set(false);
  }

  private handleMinimum(safetyFactorResults: SafetyFactorResultModel[], designFactors, type) {
    this.previousView = 'minimum';
    this.columnDefinitions = [];
    this.columnDefs = [];
    this.results = [];
    this.loadCases = this.types;
    this.loadCases = this.loadCases.filter(x => x.label !== 'Summary');
    this.loadCases.unshift({ label: 'Summary', value: 'summary' });

    let selectedType = type.value ?? type;
    if (selectedType?.startsWith('apiCollapse')) {
      selectedType = safetyFactorResults[0].apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse';
    }
    this.setAxisNames(selectedType);

    this.safetyFactorResultsStore.update(state => ({ ...state, selectedResultDisplay: 'grid' }));
    this.selectedResultDisplay = 'grid';

    const typeLabel = this.types.find(t => t.value === selectedType || (selectedType.startsWith('apiCollapse') && t.value === 'apiCollapse'))?.label ?? selectedType;
    const depthUnit = this.userUnits.longLengths + "MD";
    const tolerance = 0.001;

    this.columnDefinitions = [
      {
        header: 'Load Name', field: 'loadName',
        valueFormatter: (params: any) => params
      },
      {
        header: typeLabel, field: selectedType,
        valueFormatter: (params: any) => params
      }
    ];

    this.results.push({
      loadName: 'Design Factor',
      [selectedType]: this.getDesignFactor(designFactors, selectedType, safetyFactorResults[0].apiCollapseType)
    });

    safetyFactorResults.forEach(load => {
      let smallestValue = 10000;
      let depthMd = 0;
      if (!load.safetyFactorResults || load.safetyFactorResults.length === 0) {
        return;
      }
      load.safetyFactorResults.forEach(element => {
        if (element[selectedType] < smallestValue) {
          smallestValue = element[selectedType];
          depthMd = element.measuredDepth;
        }
      });

      const burstValueAtDepth = load.safetyFactorResults.find(x => Math.abs(x.measuredDepth - depthMd) < tolerance).barlowBurst;
      const collapseValueAtDepth = load.safetyFactorResults.find(x => Math.abs(x.measuredDepth - depthMd) < tolerance)[selectedType];
      const designFactorCheck = this.safetyFactorResultsStore.state.includeCharacters && smallestValue < this.getDesignFactor(designFactors, selectedType, safetyFactorResults[0].apiCollapseType, burstValueAtDepth, collapseValueAtDepth) - 0.000001 ? ' *' : '';
      const connectionMarker = this.getConnectionWeakerMarker(load.loadCaseName, depthMd, selectedType, smallestValue);

      this.results.push({
        loadName: load.loadCaseName,
        [selectedType]: this.safetyFactorResultsStore.state.includeCharacters
          ? this.roundValue(smallestValue, 3) + designFactorCheck + ' - @ - ' + this.formatDecimal(depthMd, 10) + ` ${depthUnit}` + connectionMarker
          : this.roundValue(smallestValue, 3)
      });
    });

    this.isLoading.set(false);
  }

  private handleMinimumSummary(safetyFactorResults: any[], designFactors) {
    this.previousView = 'summary';
    this.columnDefinitions = [];
    this.columnDefs = [];
    this.results = [];
    this.loadCases = this.types;
    this.loadCases = this.loadCases.filter(x => x.label !== 'Summary');
    this.loadCases.unshift({ label: 'Summary', value: 'summary' });

    this.safetyFactorResultsStore.update(state => ({ ...state, selectedResultDisplay: 'grid' }));
    this.selectedResultDisplay = 'grid';

    this.columnDefinitions = [
      {
        header: 'Load Name', field: 'loadName',
        valueFormatter: (params: any) => params
      },
      {
        header: 'VME (Triaxial)', field: 'vonMisesEquivalent',
        valueFormatter: (params: any) => params
      },
      {
        header: 'Barlow Burst', field: 'barlowBurst',
        valueFormatter: (params: any) => params
      },
      {
        header: 'API Collapse', field: safetyFactorResults[0].apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse',
        valueFormatter: (params: any) => params
      },
      {
        header: 'Axial', field: 'pureAxial',
        valueFormatter: (params: any) => params
      },
    ];

    safetyFactorResults.forEach(load => {
      let smallestBurst = 10000;
      let smallestAxial = 10000;
      let smallestVme = 10000;
      let smallestCollapse = 10000;
      if (!load.safetyFactorResults || load.safetyFactorResults.length === 0) {
        return;
      }
      const firstDepth = load.safetyFactorResults[0].measuredDepth;
      let depthBurst = firstDepth;
      let depthAxial = firstDepth;
      let depthCollapse = firstDepth;
      let depthVme = firstDepth;
      let collapse;
      load.safetyFactorResults.forEach(element => {
        if (element['barlowBurst'] < smallestBurst) {
          smallestBurst = element['barlowBurst'];
          depthBurst = element.measuredDepth.toFixed(3);
        }

        if (element['pureAxial'] < smallestAxial) {
          smallestAxial = element['pureAxial'];
          depthAxial = element.measuredDepth.toFixed(3);
        }

        if (element['vonMisesEquivalent'] < smallestVme) {
          smallestVme = element['vonMisesEquivalent'];
          depthVme = element.measuredDepth.toFixed(3);
        }

        collapse = safetyFactorResults[0].apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse'
        if (element[collapse] < smallestCollapse) {
          smallestCollapse = element[collapse];
          depthCollapse = element.measuredDepth.toFixed(3);
        }
      });

      if (this.results.length < 1) {
        this.results.push({
          loadName: 'Design Factor',
          barlowBurst: designFactors['barlowBurst'],
          [collapse]: designFactors['apiCollapse'],
          pureAxial: Math.min(designFactors['pureTension'], designFactors['pureCompression']),
          vonMisesEquivalent: `${designFactors['vonMisesBurst'] + ' (B) / ' + designFactors['vonMisesCollapse'] + ' (C)'}`
        });
      }

      const tolerance = this.userUnits.longLengths == 'ft' ? 0.001 : 0.01;
      const loadIndex = this.axialResults.findIndex(x => x.loadCaseName == load.loadCaseName);
      const depthIndex = this.axialResults[loadIndex].axialResults.findIndex(x => Math.abs(x.measuredDepth - depthAxial) < tolerance);
      const axialForceValue = this.axialResults[loadIndex].axialResults[depthIndex].axialLoad;
      const tensionCompression = axialForceValue < 0 ? ' - Compression' : '';
      const depthUnit = this.userUnits.longLengths + "MD";
      const burstValueAtDepth = load.safetyFactorResults.find(x => Math.abs(x.measuredDepth - depthBurst) < tolerance).barlowBurst;
      const collapseValueAtDepth = load.safetyFactorResults.find(x => Math.abs(x.measuredDepth - depthCollapse) < tolerance)[collapse];

      this.results.push({
        loadName: load.loadCaseName,
        barlowBurst: this.safetyFactorResultsStore.state.includeCharacters ? this.roundValue(smallestBurst, 3) + (smallestBurst < this.getDesignFactor(designFactors, 'barlowBurst', safetyFactorResults[0].apiCollapseType) - 0.000001 ? ' *' : '') + ' - @ - ' + this.formatDecimal(depthBurst, 10) + ` ${depthUnit}` + this.getConnectionWeakerMarker(load.loadCaseName, depthBurst, 'barlowBurst', smallestBurst) : this.roundValue(smallestBurst, 3),
        [collapse]: this.safetyFactorResultsStore.state.includeCharacters ? this.roundValue(smallestCollapse, 3) + (smallestCollapse < this.getDesignFactor(designFactors, 'apiCollapse', safetyFactorResults[0].apiCollapseType) - 0.000001 ? ' *' : '') + ' - @ - ' + this.formatDecimal(depthCollapse, 10) + ` ${depthUnit}` + this.getConnectionWeakerMarker(load.loadCaseName, depthCollapse, collapse, smallestCollapse) : this.roundValue(smallestCollapse, 3),
        pureAxial: this.safetyFactorResultsStore.state.includeCharacters ? this.roundValue(smallestAxial, 3) + (smallestAxial < this.getDesignFactor(designFactors, 'pureAxial', safetyFactorResults[0].apiCollapseType) - 0.000001 ? ' *' : '') + ' - @ - ' + this.formatDecimal(depthAxial, 10) + ` ${depthUnit}` + tensionCompression + this.getConnectionWeakerMarker(load.loadCaseName, depthAxial, 'pureAxial', smallestAxial) : this.roundValue(smallestAxial, 3),
        vonMisesEquivalent: this.safetyFactorResultsStore.state.includeCharacters ? this.roundValue(smallestVme, 3) + (smallestVme < this.getDesignFactor(designFactors, 'vonMisesEquivalent', safetyFactorResults[0].apiCollapseType, burstValueAtDepth, collapseValueAtDepth) - 0.000001 ? ' *' : '') + ' - @ - ' + this.formatDecimal(depthVme, 10) + ` ${depthUnit}` : this.roundValue(smallestVme, 3)
      });
    });

    if (!this.safetyFactorResultsStore.state.selectedLoadCase || !this.loadCases.find(l => l.value.tubularStringId == this.safetyFactorResultsStore.state.selectedLoadCase?.value.tubularStringId)) {
      this.safetyFactorResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      this.plotName = this.setPlotName();
    }
    this.signalRChange = !this.signalRChange;
    this.isLoading.set(false);
  }

  private getDesignFactor(designFactors: any, selectedType, apiCollapseType, burstValueAtDepth?: number, collapseValueAtDepth?: number) {
    if (selectedType.startsWith('apiCollapse')) {
      selectedType = apiCollapseType === 'CollapseApi2018' ? 'apiCollapse2015' : 'apiCollapse'
    }
    let df = selectedType;
    if (df === 'pureAxial') {
      df = Math.min(designFactors['pureTension'], designFactors['pureCompression']);
    } else if (df === 'vonMisesEquivalent') {
      df = burstValueAtDepth < 100 ? designFactors['vonMisesBurst'] : collapseValueAtDepth < 100 ? designFactors['vonMisesCollapse'] : designFactors['vonMisesBurst'];
    } else if (df === 'apiCollapse' || df === 'apiCollapse2015') {
      df = designFactors['apiCollapse'];
    } else if (df === 'barlowBurst') {
      df = designFactors['barlowBurst'];
    }

    return df;
  }

  private addDesignFactor(selectedType, gridDesignFactorName, designFactors, burstValueAtDepth?: number, collapseValueAtDepth?: number) {
    let df = selectedType;
    if (df === 'pureAxial') {
      df = designFactors['pureTension'] < designFactors['pureCompression'] ? 'pureTension' : 'pureCompression';
    } else if (df === 'vonMisesEquivalent') {
      df = 'vonMisesBurst';
    } else if (df.startsWith('apiCollapse')) {
      df = 'apiCollapse';
    }

    this.columnDefs.push({ header: gridDesignFactorName, field: selectedType });
    this.columnDefinitions.push(
      {
        header: gridDesignFactorName, field: selectedType,
        valueFormatter: (params: any) => params
      }
    );
    this.results = this.results.map(x => {
      const designFactor = {
        [selectedType]: selectedType == 'vonMisesEquivalent' ? burstValueAtDepth < 100 ? designFactors['vonMisesBurst'] : collapseValueAtDepth < 100 ? designFactors['vonMisesCollapse'] : designFactors['vonMisesBurst'] : designFactors[df],
        measuredDepth: x.measuredDepth,
        trueVerticalDepth: x.trueVerticalDepth
      }
      return { ...x, ...designFactor }
    });
  }

  private setAxisNames(selectedType: any) {
    switch (selectedType) {
      case 'barlowBurst': {
        this.gridDesignFactorName = 'Burst DF';
        this.xAxisTitle = 'Burst Safety Factor';
        break;
      }
      case 'apiCollapse': {
        this.gridDesignFactorName = 'Collapse DF';
        this.xAxisTitle = 'Collapse Safety Factor';
        break;
      }
      case 'pureAxial': {
        this.gridDesignFactorName = 'Axial DF';
        this.xAxisTitle = 'Axial Safety Factor';
        break;
      }
      case 'vonMisesEquivalent': {
        this.gridDesignFactorName = 'VME (Triaxial) DF';
        this.xAxisTitle = 'VME (Triaxial) Safety Factor';
        break;
      }
      default: {
        break;
      }
    }
  }

  private roundValue(value, decimalPoints) {
    let fixed = decimalPoints || 0;
    fixed = Math.pow(10, fixed);
    return Math.floor(value * fixed) / fixed;
  }

  private getConnectionWeakerMarker(loadCaseName: string, depthMd: number | string, pipeType: string, pipeValue: number): string {
    if (!this.safetyFactorResultsStore.state.includeCharacters) {
      return '';
    }
    if (!this.connectionSafetyFactorResults?.length) {
      return '';
    }
    const connectionField = pipeType === 'barlowBurst' ? 'burst'
      : pipeType?.startsWith('apiCollapse') ? 'collapse'
      : pipeType === 'pureAxial' ? 'axial'
      : null;
    if (!connectionField) {
      return '';
    }
    const load = this.connectionSafetyFactorResults.find(l => l.loadCaseName === loadCaseName);
    if (!load?.connectionSafetyFactorResults?.length) {
      return '';
    }
    const tolerance = this.userUnits.longLengths === 'ft' ? 0.001 : 0.01;
    const depthNum = typeof depthMd === 'string' ? Number(depthMd) : depthMd;
    const point = load.connectionSafetyFactorResults.find(x => Math.abs(x.measuredDepth - depthNum) < tolerance);
    if (!point) {
      return '';
    }
    const connectionValue = point[connectionField];
    const connectionDesignFactor = this.getConnectionDesignFactor(pipeType);
    if (connectionDesignFactor == null || connectionValue == null) {
      return '';
    }

    const isConnectionFailed = connectionValue < connectionDesignFactor - 0.000001;
    const isConnectionWeakerThanPipe = connectionValue < pipeValue;

    return isConnectionFailed && isConnectionWeakerThanPipe ? ' - Conn' : '';
  }

  private getConnectionDesignFactor(pipeType: string): number | null {
    const connectionDesignFactors = this.designFactors?.connection;
    if (!connectionDesignFactors) {
      return null;
    }

    if (pipeType === 'barlowBurst') {
      return connectionDesignFactors['burstLeak'];
    }

    if (pipeType?.startsWith('apiCollapse')) {
      return connectionDesignFactors['collapse'];
    }

    if (pipeType === 'pureAxial') {
      return Math.min(connectionDesignFactors['tension'], connectionDesignFactors['compression']);
    }

    return null;
  }

  private setDepths() {
    return [
      {
        header: `MD (${this.depthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.depthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
    ]
  }

  public toggleCharacterView(e) {
    this.includeCharacters = e.checked;
    this.safetyFactorResultsStore.update(state => ({ ...state, includeCharacters: e.checked }));

    if (this.safetyFactorResultsStore.state.selectedMode == "single") {
      this.handleSingleSafetyFactorLoad(this.resultsBackup, false);
    } else if (this.safetyFactorResultsStore.state.selectedMode == 'multiple') {
      this.handleMultiSafetyFactorLoad(this.resultsBackup, this.designFactors)
    } else if (this.safetyFactorResultsStore.state.selectedMode == 'minimum' && this.minimumResultType !== 'barlowBurst' && !this.minimumResultType.startsWith('apiCollapse') &&
      this.minimumResultType !== 'pureAxial' && this.minimumResultType !== 'vonMisesEquivalent') {
      this.handleMinimumSummary(this.resultsBackup, this.designFactors);
    }

    if (this.minimumResultType == 'barlowBurst' || this.minimumResultType.startsWith('apiCollapse') ||
      this.minimumResultType == 'pureAxial' || this.minimumResultType == 'vonMisesEquivalent') {
      this.handleMinimum(this.resultsBackup, this.designFactors, this.safetyFactorResultsStore.state.selectedLoadCase);
    }
  }

  ngOnDestroy() {
    this.signalRfunc = null;
    this._subscriptions.unsubscribe();
  }
}
